<div class="tour-modal" role="document">

  <!-- ───────── Header ───────── -->
  <div class="tour-modal__header">
    <div class="tour-modal__header-left">
      <div class="tour-modal__eyebrow">
        <mat-icon class="tour-modal__eyebrow-icon">explore</mat-icon>
        Onboarding
      </div>
      <h2 class="tour-modal__title">Take a product tour</h2>
    </div>
    <button class="tour-modal__exit-btn" (click)="exit()" matRipple aria-label="Exit tour">
      <mat-icon>close</mat-icon>
      <span>Exit tour</span>
    </button>
  </div>

  <!-- ───────── Progress bar ───────── -->
  <div class="tour-modal__progress" *ngIf="summary$ | async as summary">
    <div class="tour-modal__progress-labels">
      <span class="tour-modal__progress-text">
        <strong>{{ summary.completedItems }}</strong> of {{ summary.totalItems }} completed
      </span>
      <span class="tour-modal__progress-pct">{{ summary.completionPercentage | number:'1.0-0' }}%</span>
    </div>
    <div class="tour-modal__progress-track">
      <div class="tour-modal__progress-fill" [style.width.%]="summary.completionPercentage"></div>
    </div>
  </div>

  <!-- ───────── Body (split layout) ───────── -->
  <div class="tour-modal__body">

    <!-- Left: Step list -->
    <div class="tour-modal__steps-panel">
      <calsoft-tour-step-list></calsoft-tour-step-list>
    </div>

    <!-- Right: Active step detail -->
    <div class="tour-modal__preview-panel">
      <ng-container *ngIf="currentStep$ | async as step; else emptyPreview">

        <!-- All done state -->
        <ng-container *ngIf="isAllDone$ | async; else stepDetail">
          <div class="tour-preview__done">
            <div class="tour-preview__done-icon">
              <mat-icon>task_alt</mat-icon>
            </div>
            <h3 class="tour-preview__done-title">You're all set!</h3>
            <p class="tour-preview__done-sub">You've completed all the onboarding steps. You're ready to get the most out of the platform.</p>
            <button class="tour-preview__done-btn" (click)="exit()" matRipple>
              <mat-icon>rocket_launch</mat-icon>
              Get Started
            </button>
          </div>
        </ng-container>

        <!-- Normal step detail -->
        <ng-template #stepDetail>
          <div class="tour-preview">

            <!-- Step icon / illustration area -->
            <div class="tour-preview__icon-wrap">
              <mat-icon class="tour-preview__icon">{{ step.iconName || 'lightbulb' }}</mat-icon>
            </div>

            <!-- Step metadata -->
            <div class="tour-preview__index" *ngIf="currentIndex$ | async as idx">
              Step {{ idx + 1 }}
            </div>

            <h3 class="tour-preview__title">{{ step.itemTitle }}</h3>

            <p class="tour-preview__description" *ngIf="step.itemDescription">
              {{ step.itemDescription }}
            </p>

            <!-- Status badge -->
            <div class="tour-preview__status">
              <span class="tour-badge" [class]="'tour-badge--' + step.status.toLowerCase()">
                <mat-icon class="tour-badge__icon">{{ getBadgeIcon(step.status) }}</mat-icon>
                {{ getStatusLabel(step) }}
              </span>
            </div>

            <!-- Action button -->
            <div class="tour-preview__actions">
              <button
                *ngIf="step.actionName && (step.actionUrl || step.routePath)"
                class="tour-preview__action-btn"
                (click)="navigateToAction(step)"
                matRipple>
                <mat-icon>arrow_forward</mat-icon>
                {{ step.actionName }}
              </button>
            </div>

          </div>
        </ng-template>

      </ng-container>

      <ng-template #emptyPreview>
        <div class="tour-preview tour-preview--empty">
          <mat-icon>map</mat-icon>
          <p>Select a step to get started.</p>
        </div>
      </ng-template>
    </div>

  </div>

  <!-- ───────── Footer ───────── -->
  <div class="tour-modal__footer">
    <div class="tour-modal__footer-left">
      <button
        class="tour-btn tour-btn--ghost"
        [disabled]="!(hasPrev$ | async)"
        (click)="prev()"
        matRipple>
        <mat-icon>chevron_left</mat-icon>
        Previous
      </button>
    </div>

    <div class="tour-modal__footer-center">
      <ng-container *ngIf="currentIndex$ | async as idx">
        <ng-container *ngIf="steps$ | async as steps">
          <span class="tour-modal__step-counter">{{ idx + 1 }} / {{ steps.length }}</span>
        </ng-container>
      </ng-container>
    </div>

    <div class="tour-modal__footer-right">
      <button
        *ngIf="!(isCurrentCompleted$ | async)"
        class="tour-btn tour-btn--ghost"
        (click)="skip()"
        matRipple>
        Skip
      </button>

      <ng-container *ngIf="isAllDone$ | async; else normalNext">
        <button class="tour-btn tour-btn--primary" (click)="exit()" matRipple>
          <mat-icon>check_circle</mat-icon>
          Finish Tour
        </button>
      </ng-container>

      <ng-template #normalNext>
        <ng-container *ngIf="currentStep$ | async as step">
          <button
            *ngIf="!(isCurrentCompleted$ | async)"
            class="tour-btn tour-btn--primary"
            [disabled]="loading$ | async"
            (click)="step.linkedStepGuideId ? launchGuide() : complete()"
            matRipple>
            <mat-icon [class.spinning]="loading$ | async">
              {{ (loading$ | async) ? 'autorenew' : (step.linkedStepGuideId ? 'play_arrow' : 'check') }}
            </mat-icon>
            {{ (loading$ | async) ? 'Loading...' : (step.linkedStepGuideId ? 'Start guide' : 'Mark done & continue') }}
          </button>
        </ng-container>

        <button
          *ngIf="isCurrentCompleted$ | async"
          class="tour-btn tour-btn--ghost"
          (click)="next()"
          [disabled]="!(hasNext$ | async)"
          matRipple>
          Next step
          <mat-icon>chevron_right</mat-icon>
        </button>

        <ng-container *ngIf="currentStep$ | async as step">
          <button
            *ngIf="(isCurrentCompleted$ | async) && step.linkedStepGuideId"
            class="tour-btn tour-btn--ghost"
            (click)="launchGuide()"
            matRipple>
            <mat-icon>replay</mat-icon>
            Start again
          </button>
        </ng-container>
      </ng-template>
    </div>
  </div>

</div>
